/**
 * REIT Symbol Patterns
 * 
 * Patterns for identifying Real Estate Investment Trusts.
 * REIT dividends are typically taxed as ordinary income (not qualified).
 */

import type { SymbolPattern } from '../types.js';

/**
 * REIT ETF symbols
 */
export const REIT_ETF_SYMBOLS = [
  'VNQ',   // Vanguard Real Estate ETF
  'VNQI',  // Vanguard Global ex-U.S. Real Estate ETF
  'SCHH',  // Schwab U.S. REIT ETF
  'FREL',  // Fidelity MSCI Real Estate Index ETF
  'IYR',   // iShares U.S. Real Estate ETF
  'XLRE',  // Real Estate Select Sector SPDR Fund
  'RWR',   // SPDR Dow Jones REIT ETF
  'USRT',  // iShares Core U.S. REIT ETF
];

/**
 * Individual REIT stock symbols (commonly held)
 */
export const INDIVIDUAL_REIT_SYMBOLS = [
  'O',     // Realty Income Corporation
  'SPG',   // Simon Property Group
  'AMT',   // American Tower Corporation
  'PLD',   // Prologis
  'CCI',   // Crown Castle International
  'EQIX',  // Equinix
  'DLR',   // Digital Realty Trust
  'PSA',   // Public Storage
  'WELL',  // Welltower Inc.
  'AVB',   // AvalonBay Communities
  'EQR',   // Equity Residential
  'ARE',   // Alexandria Real Estate Equities
  'VTR',   // Ventas Inc.
  'SUI',   // Sun Communities
  'EXR',   // Extra Space Storage
  'MAA',   // Mid-America Apartment Communities
  'UDR',   // UDR Inc.
  'ESS',   // Essex Property Trust
  'HST',   // Host Hotels & Resorts
  'PEAK',  // Healthpeak Properties
  'KIM',   // Kimco Realty
  'REG',   // Regency Centers
  'BXP',   // Boston Properties
  'CPT',   // Camden Property Trust
  'IRM',   // Iron Mountain
  'CUBE',  // CubeSmart
  'RHP',   // Ryman Hospitality Properties
  'NNN',   // National Retail Properties
  'WPC',   // W. P. Carey Inc.
  'STAG',  // STAG Industrial
  'STOR',  // STORE Capital
  'FR',    // First Industrial Realty Trust
];

/**
 * All REIT symbol patterns
 */
export const REIT_SYMBOL_PATTERNS: SymbolPattern[] = [
  // ETF symbols
  ...REIT_ETF_SYMBOLS.map(sym => ({
    pattern: new RegExp(`^${sym}$`, 'i'),
    description: `REIT ETF: ${sym}`,
  })),
  // Individual REIT symbols
  ...INDIVIDUAL_REIT_SYMBOLS.map(sym => ({
    pattern: new RegExp(`^${sym}$`, 'i'),
    description: `Individual REIT: ${sym}`,
  })),
];

/**
 * Description pattern for REIT detection
 */
export const REIT_DESCRIPTION_PATTERN = /\bREIT\b/i;

/**
 * Check if a symbol or description indicates a REIT
 */
export function isReitSymbol(symbol: string, description?: string): boolean {
  const sym = symbol?.toUpperCase() || '';
  const desc = description || '';
  
  // Check symbol patterns
  if (REIT_SYMBOL_PATTERNS.some(p => p.pattern.test(sym))) {
    return true;
  }
  
  // Check description for "REIT" keyword
  if (REIT_DESCRIPTION_PATTERN.test(desc)) {
    return true;
  }
  
  return false;
}
